/**
 * @file Event Manager Class
 * @description Centralized event system with event delegation and memory management
 */

/**
 * Event Manager Class
 * Manages application events with centralized handling
 */
export class EventManager {
  constructor() {
    this.events = new Map();
    this.delegatedEvents = new Map();
    this.globalListeners = new Map();
    this.isInitialized = false;
  }

  /**
   * Get singleton instance of EventManager
   * @returns {EventManager} The singleton instance
   */
  static getInstance() {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager();
    }
    return EventManager.instance;
  }

  /**
   * Initialize the event manager
   */
  async init() {
    try {
      console.log("📡 Initializing Event Manager...");

      // Setup global event listeners
      this.setupGlobalListeners();

      // Setup delegated events
      this.setupDelegatedEvents();

      this.isInitialized = true;
      console.log("✅ Event Manager initialized");
    } catch (error) {
      console.error("❌ Event Manager initialization failed:", error);
      throw error;
    }
  }

  /**
   * Setup global event listeners
   */
  setupGlobalListeners() {
    // Window events
    this.globalListeners.set("resize", this.handleResize.bind(this));
    this.globalListeners.set("scroll", this.handleScroll.bind(this));
    this.globalListeners.set("beforeunload", this.handleBeforeUnload.bind(this));

    // Document events
    this.globalListeners.set("DOMContentLoaded", this.handleDOMContentLoaded.bind(this));
    this.globalListeners.set("click", this.handleGlobalClick.bind(this));
    this.globalListeners.set("keydown", this.handleKeydown.bind(this));

    // Register global listeners
    for (const [eventName, handler] of this.globalListeners) {
      window.addEventListener(eventName, handler, { passive: true });
    }
  }

  /**
   * Setup delegated events
   */
  setupDelegatedEvents() {
    // Chat bubble events
    this.delegatedEvents.set("chat-bubble-click", {
      selector: ".chat-bubble",
      event: "click",
      handler: this.handleChatBubbleClick.bind(this),
    });

    // Map marker events
    this.delegatedEvents.set("map-marker-click", {
      selector: ".map-marker",
      event: "click",
      handler: this.handleMapMarkerClick.bind(this),
    });

    // Form submission events
    this.delegatedEvents.set("form-submit", {
      selector: "form",
      event: "submit",
      handler: this.handleFormSubmit.bind(this),
    });

    // Link click events
    this.delegatedEvents.set("link-click", {
      selector: "a[href]",
      event: "click",
      handler: this.handleLinkClick.bind(this),
    });

    // Register delegated events
    for (const [eventName, config] of this.delegatedEvents) {
      document.addEventListener(config.event, config.handler, {
        passive: true,
      });
    }
  }

  /**
   * Emit a custom event
   * @param {string} eventName - Event name
   * @param {Object} data - Event data
   */
  emit(eventName, data = {}) {
    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: true,
      cancelable: true,
    });

    document.dispatchEvent(event);

    // Log in development
    if (process.env.NODE_ENV === "development") {
      console.log(`📡 Event emitted: ${eventName}`, data);
    }
  }

  /**
   * Listen for an event
   * @param {string} eventName - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event options
   * @returns {Function} Unsubscribe function
   */
  on(eventName, handler, options = {}) {
    const wrappedHandler = (event) => {
      handler(event.detail || event);
    };

    document.addEventListener(eventName, wrappedHandler, options);

    // Store for cleanup
    if (!this.events.has(eventName)) {
      this.events.set(eventName, new Set());
    }
    this.events.get(eventName).add(wrappedHandler);

    // Return unsubscribe function
    return () => {
      document.removeEventListener(eventName, wrappedHandler);
      this.events.get(eventName).delete(wrappedHandler);
    };
  }

  /**
   * Listen for an event once
   * @param {string} eventName - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event options
   */
  once(eventName, handler, options = {}) {
    const wrappedHandler = (event) => {
      handler(event.detail || event);
      document.removeEventListener(eventName, wrappedHandler);
    };

    document.addEventListener(eventName, wrappedHandler, options);
  }

  /**
   * Remove event listener
   * @param {string} eventName - Event name
   * @param {Function} handler - Event handler
   */
  off(eventName, handler) {
    document.removeEventListener(eventName, handler);

    if (this.events.has(eventName)) {
      this.events.get(eventName).delete(handler);
    }
  }

  /**
   * Handle window resize
   * @param {Event} event - Resize event
   */
  handleResize(event) {
    this.emit("app:resize", {
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  /**
   * Handle window scroll
   * @param {Event} event - Scroll event
   */
  handleScroll(event) {
    this.emit("app:scroll", {
      scrollY: window.scrollY,
      scrollX: window.scrollX,
    });
  }

  /**
   * Handle before unload
   * @param {Event} event - Before unload event
   */
  handleBeforeUnload(event) {
    this.emit("app:beforeunload");
  }

  /**
   * Handle DOM content loaded
   * @param {Event} event - DOM content loaded event
   */
  handleDOMContentLoaded(event) {
    this.emit("app:DOMContentLoaded");
  }

  /**
   * Handle global click
   * @param {Event} event - Click event
   */
  handleGlobalClick(event) {
    this.emit("app:click", {
      target: event.target,
      x: event.clientX,
      y: event.clientY,
    });
  }

  /**
   * Handle keydown
   * @param {Event} event - Keydown event
   */
  handleKeydown(event) {
    this.emit("app:keydown", {
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
    });
  }

  /**
   * Handle chat bubble click
   * @param {Event} event - Click event
   */
  handleChatBubbleClick(event) {
    const bubble = event.target.closest(".chat-bubble");
    if (bubble) {
      this.emit("chat-bubble:click", {
        element: bubble,
        type: bubble.dataset.type,
        action: bubble.dataset.action,
      });
    }
  }

  /**
   * Handle map marker click
   * @param {Event} event - Click event
   */
  handleMapMarkerClick(event) {
    const marker = event.target.closest(".map-marker");
    if (marker) {
      this.emit("map-marker:click", {
        element: marker,
        id: marker.dataset.id,
        type: marker.dataset.type,
      });
    }
  }

  /**
   * Handle form submission
   * @param {Event} event - Submit event
   */
  handleFormSubmit(event) {
    const form = event.target;
    this.emit("form:submit", {
      element: form,
      action: form.action,
      method: form.method,
    });
  }

  /**
   * Handle link click
   * @param {Event} event - Click event
   */
  handleLinkClick(event) {
    const link = event.target.closest("a[href]");
    if (link) {
      this.emit("link:click", {
        element: link,
        href: link.href,
        target: link.target,
      });
    }
  }

  /**
   * Get all registered events
   * @returns {Array} Array of event names
   */
  getRegisteredEvents() {
    return Array.from(this.events.keys());
  }

  /**
   * Get all delegated events
   * @returns {Array} Array of delegated event names
   */
  getDelegatedEvents() {
    return Array.from(this.delegatedEvents.keys());
  }

  /**
   * Destroy the event manager
   */
  destroy() {
    // Remove all event listeners
    for (const [eventName, handlers] of this.events) {
      for (const handler of handlers) {
        document.removeEventListener(eventName, handler);
      }
    }

    // Remove global listeners
    for (const [eventName, handler] of this.globalListeners) {
      window.removeEventListener(eventName, handler);
    }

    // Remove delegated event listeners
    for (const [eventName, config] of this.delegatedEvents) {
      document.removeEventListener(config.event, config.handler);
    }

    // Clear collections
    this.events.clear();
    this.globalListeners.clear();
    this.delegatedEvents.clear();

    this.isInitialized = false;
    console.log("🔄 Event Manager destroyed");
  }
}
